// Документът на изписване отвсякъде (началният екран, справката, toast-ът след
// запис): модалът, анулирането с причина и редакцията. Връща `open`, прякото
// `annul`/`edit` (менюто „⋯" на реда в справката) и модалите за рендер.
import { useState } from "react";
import { App } from "antd";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { Writeoff } from "../../../../api/inventory";
import { useAnnulWriteoff } from "../../../../hooks/useInventoryWriteoffs";
import { AnnulDocumentModal } from "../../components/AnnulDocumentModal";
import {
  annulUndoNote,
  productLineItems,
  useProductById,
} from "../../components/confirm/confirmPreviews";
import { useActionToast } from "../../components/useActionToast";
import { WriteoffDocumentModal } from "./WriteoffDocumentModal";

export const useWriteoffDocument = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const showToast = useActionToast();
  const annulMutation = useAnnulWriteoff();
  const productById = useProductById();
  const [doc, setDoc] = useState<{ writeoff: Writeoff; editable: boolean; autoPrint: boolean } | null>(
    null,
  );
  const [annulTarget, setAnnulTarget] = useState<Writeoff | null>(null);

  // Списъкът носи редовете на документа → без допълнителна заявка
  const open = (writeoff: Writeoff, options?: { editable?: boolean; autoPrint?: boolean }) =>
    setDoc({
      writeoff,
      editable: options?.editable ?? true,
      autoPrint: options?.autoPrint ?? false,
    });

  // Редакцията е на собствена страница (работен лист)
  const edit = (writeoff: Writeoff) => {
    setDoc(null);
    navigate(`/inventory/writeoff/${writeoff.id}/edit`);
  };

  const confirmAnnul = (reason: string | null) => {
    if (!annulTarget) return;
    annulMutation.mutate(
      { id: annulTarget.id, reason },
      {
        onSuccess: () => {
          showToast({ text: t("inventoryPage.annulledToast", { number: annulTarget.id }) });
          setAnnulTarget(null);
          setDoc(null);
        },
        onError: (e: unknown) => {
          const err = e as { response?: { data?: { detail?: string } } };
          message.error(err.response?.data?.detail || t("inventoryPage.errDelete"));
        },
      },
    );
  };

  const modals = (
    <>
      <WriteoffDocumentModal
        open={!!doc}
        writeoff={doc?.writeoff ?? null}
        autoPrint={doc?.autoPrint}
        onClose={() => setDoc(null)}
        onEdit={doc?.editable ? () => edit(doc.writeoff) : undefined}
        onAnnul={doc?.editable ? () => setAnnulTarget(doc.writeoff) : undefined}
      />
      <AnnulDocumentModal
        open={!!annulTarget}
        title={t(
          annulTarget?.kind === "scrap"
            ? "inventoryPage.cfmAnnulScrapTitle"
            : "inventoryPage.cfmAnnulWriteoffTitle",
          { number: annulTarget?.id ?? "" },
        )}
        text={t("inventoryPage.woAnnulText")}
        confirmLabel={t(
          annulTarget?.kind === "scrap"
            ? "inventoryPage.cfmAnnulScrapConfirm"
            : "inventoryPage.cfmAnnulWriteoffConfirm",
        )}
        preview={
          annulTarget
            ? {
                caption: t("inventoryPage.cfmAnnulWriteoffCaption"),
                items: productLineItems(annulTarget.lines ?? [], productById),
              }
            : undefined
        }
        note={annulUndoNote(t)}
        saving={annulMutation.isPending}
        onCancel={() => setAnnulTarget(null)}
        onConfirm={confirmAnnul}
      />
    </>
  );

  return { open, edit, annul: setAnnulTarget, modals };
};
